import { useCallback, useRef, useState } from "react";
import { Dimensions, Platform, View } from "react-native";

import { BubbleCoordinates, OpenPosition } from "../types";
import { getEdgeCorrectedTargetXYForModal } from "../utils";

export const useGetTriggerWidthAndTargetCoordinates = (
  openPosition: OpenPosition,
  verticalOffset: number | undefined,
  horizontalOffset: number | undefined
) => {
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const [targetCoords, setTargetCoords] = useState<BubbleCoordinates | null>(
    null
  );
  const triggerRef = useRef<View>(null);
  const modalBodyRef = useRef<View>(null);

  const measureScreenPosition = useCallback(() => {
    if (triggerRef.current && modalBodyRef.current) {
      /**
       * Web and Native differ enough on how they measure that this was necessary.
       * This ensures that web doesn't get crazy with scroll offsets when we are opening
       * dropdowns etc. that are nested in scroll containers. Native just plays nice
       * with that via it's 'measure' api, but the RNW implementation seems off.
       */
      if (Platform.OS === "web") {
        const element = triggerRef.current;
        // @ts-ignore - access DOM node on web
        const rect = element.getBoundingClientRect();

        modalBodyRef.current?.measure(
          (_mbX, _mbY, modalBodyWidth, modalBodyHeight) => {
            const { width: viewportWidth, height: viewportHeight } =
              Dimensions.get("window");

            const { coords } = getEdgeCorrectedTargetXYForModal({
              coordinateArgs: {
                pageX: rect.left,
                pageY: rect.top,
                width: rect.width,
                height: rect.height,
                modalBodyHeight,
                modalBodyWidth,
                openPosition,
                verticalOffset,
                horizontalOffset,
              },
              viewportHeight,
              viewportWidth,
            });
            setTriggerWidth(rect.width);
            setTargetCoords(coords);
          }
        );
      } else {
        triggerRef.current.measure((_x, _y, width, height, pageX, pageY) => {
          modalBodyRef.current?.measure(
            (
              _mbX,
              _mbY,
              modalBodyWidth,
              modalBodyHeight,
              _mbPageX,
              _mbPageY
            ) => {
              const { width: viewportWidth, height: viewportHeight } =
                Dimensions.get("window");

              const { coords } = getEdgeCorrectedTargetXYForModal({
                coordinateArgs: {
                  pageX,
                  pageY,
                  width,
                  height,
                  modalBodyHeight,
                  modalBodyWidth,
                  openPosition,
                  verticalOffset,
                  horizontalOffset,
                },
                viewportHeight,
                viewportWidth,
              });
              setTriggerWidth(width);
              setTargetCoords(coords);
            }
          );
        });
      }
    }
  }, [horizontalOffset, openPosition, verticalOffset]);

  return {
    triggerWidth,
    targetCoords,
    measureScreenPosition,
    triggerRef,
    modalBodyRef,
  };
};
